import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { Col, Container, Row, Spinner } from 'react-bootstrap'
import { SelectDropdown, type SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import { FormFeedback } from '../../../components/Form/FormFeedback'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useGetIntegrationUsersQuery } from '../../../store/integrations/integrations'
import { useMeetingUsers } from '../../../apps/professional-app/fa/MeetingSummary/hooks/users'
import { selectRoomSession } from '../../../store/meetingSummary/roomSession'
import { getContactDropdownComponent } from '../ContactDropdownItems'
import { type IntegrationServiceId } from '../../../common/integrations'
import { getContactOptions } from './utils'
import { INVALID_REFRESH_TOKEN_INTEGRATION, INVALID_WEALTHBOX_INTEGRATION } from '../../../common/errorMessages'
import { useContactLabel } from './V2/hooks'

const INVALID_INTEGRATION_ERROR_MESSAGES = [INVALID_REFRESH_TOKEN_INTEGRATION, INVALID_WEALTHBOX_INTEGRATION]

export interface ContactDropdownProps {
  serviceId: IntegrationServiceId
  onContactOptionSelected?: (option: SelectOption) => void
  fieldName?: string
  contactOptions?: SelectOption[]
  onSearchInputChange?: (searchInput: string) => Promise<void>
  initialSelectedOptionExternalId?: string
  showLabel?: boolean
  isLoading?: boolean
}

interface StandaloneDropdownContentProps {
  serviceId: IntegrationServiceId
  contactLabel: string
  onContactOptionSelected?: (option: SelectOption) => void
  initialSelectedOptionExternalId?: string
  showLabel: boolean
  isLoading: boolean
}

const StandaloneDropdownContent: React.FC<StandaloneDropdownContentProps> = ({
  serviceId,
  contactLabel,
  onContactOptionSelected,
  initialSelectedOptionExternalId,
  showLabel,
  isLoading,
}) => {
  const accountId = useAppSelector(selectAccountId)
  const [searchInput, setSearchInput] = useState<string | null>(null)

  const filters = useMemo(() => {
    if (searchInput !== null) {
      return { name: searchInput }
    }
    if (initialSelectedOptionExternalId) {
      return { externalIds: [initialSelectedOptionExternalId] }
    }
    return undefined
  }, [searchInput, initialSelectedOptionExternalId])

  const {
    data: contactsData,
    isError,
    error,
    isLoading: isQueryLoading,
    isFetching,
  } = useGetIntegrationUsersQuery({ accountId, connector: serviceId, filters: filters ?? {} }, { skip: !accountId || !serviceId || !filters })

  const contactOptions = useMemo(() => {
    if (!contactsData?.data) {
      return []
    }
    return getContactOptions(contactsData.data, serviceId)
  }, [contactsData, serviceId])

  const initialOption = useMemo(() => {
    if (!initialSelectedOptionExternalId || searchInput !== null || contactOptions.length === 0) {
      return null
    }
    return contactOptions[0]
  }, [initialSelectedOptionExternalId, searchInput, contactOptions])

  useEffect(() => {
    if (initialOption) {
      onContactOptionSelected?.(initialOption)
    }
  }, [initialOption, onContactOptionSelected])

  const handleSearchInputChange = useCallback(async (input: string) => {
    setSearchInput(input)
  }, [])

  const isInitialLoading = !!initialSelectedOptionExternalId && !initialOption && isQueryLoading
  const isInvalidIntegration = isError && INVALID_INTEGRATION_ERROR_MESSAGES.some((msg) => JSON.stringify(error).match(msg))

  return (
    <>
      {isInitialLoading && (
        <div className='d-flex align-items-center justify-content-center'>
          <Spinner />
        </div>
      )}
      {!isInitialLoading && isInvalidIntegration && (
        <Container className='p-0'>
          <Row>
            <Col>Your integration is invalid, please reconnect your integration:</Col>
          </Row>
        </Container>
      )}
      {!isInitialLoading && !isInvalidIntegration && (
        <div>
          {showLabel && <div className='lead-extra-small'>{contactLabel}</div>}
          <SelectDropdown
            initialSelectedOption={initialOption}
            options={contactOptions}
            className='py-2 zocks-px-0.75'
            onOptionSelected={(option: SelectOption) => onContactOptionSelected?.(option)}
            DropDownItemOptionComponent={getContactDropdownComponent(serviceId as IntegrationServiceId)}
            searchable
            onSearchInputChange={handleSearchInputChange}
            placeholder={`Select your ${contactLabel}`}
            loading={isFetching || isLoading}
          />
        </div>
      )}
    </>
  )
}

interface FormDropdownContentProps {
  serviceId: IntegrationServiceId
  contactLabel: string
  fieldName: string
  contactOptions: SelectOption[]
  onSearchInputChange?: (searchInput: string) => Promise<void>
  onContactOptionSelected?: (option: SelectOption) => void
  showLabel: boolean
  isLoading: boolean
}

const FormDropdownContent: React.FC<FormDropdownContentProps> = ({
  serviceId,
  contactLabel,
  fieldName,
  contactOptions: externalContactOptions,
  onSearchInputChange,
  onContactOptionSelected,
  showLabel,
  isLoading,
}) => {
  const accountId = useAppSelector(selectAccountId)
  const { control, setValue } = useFormContext()
  const roomSession = useAppSelector(selectRoomSession)
  const { usersByRoles } = useMeetingUsers(roomSession)

  const integration = useMemo(() => {
    if (!usersByRoles) {
      return null
    }

    const clients = usersByRoles['Clients']

    if (!clients || clients.length !== 1) {
      return null
    }

    return clients[0].integrations?.find((i) => i.serviceId === serviceId) || null
  }, [usersByRoles, serviceId])

  const { isLoading: isContactsLoading, data: contactsData } = useGetIntegrationUsersQuery(
    {
      accountId,
      connector: serviceId,
      filters: integration?.externalId ? { externalIds: [integration?.externalId] } : {},
    },
    { skip: !accountId || !serviceId || !integration },
  )

  const suggestedContact = useMemo(() => {
    if (!contactsData?.data) {
      return null
    }
    const options = getContactOptions(contactsData.data, serviceId)
    return options[0] || null
  }, [contactsData, serviceId])

  useEffect(() => {
    if (!suggestedContact?.id) {
      return
    }

    setValue(fieldName, suggestedContact.id)
    onContactOptionSelected?.(suggestedContact)
  }, [suggestedContact, setValue, fieldName, onContactOptionSelected])

  const dropdownOptions = useMemo(() => {
    const options = [...externalContactOptions]
    if (suggestedContact && !options.find((option) => option.id === suggestedContact.id)) {
      options.push(suggestedContact)
    }
    return options
  }, [externalContactOptions, suggestedContact])

  return (
    <>
      {showLabel && <div className='lead-extra-small'>{contactLabel}</div>}
      <Controller
        name={fieldName}
        control={control}
        rules={{ required: `${contactLabel} is required.` }}
        render={({ field, fieldState }) => (
          <>
            <SelectDropdown
              initialSelectedOption={suggestedContact}
              options={dropdownOptions}
              className='py-2 zocks-px-0.75'
              onOptionSelected={(option: SelectOption) => {
                field.onChange(option.id)
                onContactOptionSelected?.(option)
              }}
              DropDownItemOptionComponent={getContactDropdownComponent(serviceId as IntegrationServiceId)}
              searchable
              onSearchInputChange={onSearchInputChange}
              placeholder={`Select your ${contactLabel}`}
              loading={isLoading || isContactsLoading}
            />
            {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
          </>
        )}
      />
    </>
  )
}

export const ContactDropdown: React.FC<ContactDropdownProps> = ({
  serviceId,
  onContactOptionSelected,
  fieldName,
  contactOptions = [],
  onSearchInputChange,
  initialSelectedOptionExternalId,
  showLabel = true,
  isLoading = false,
}) => {
  const { contactLabel } = useContactLabel(serviceId)

  return fieldName ? (
    <FormDropdownContent
      serviceId={serviceId}
      contactLabel={contactLabel}
      fieldName={fieldName}
      contactOptions={contactOptions}
      onSearchInputChange={onSearchInputChange}
      onContactOptionSelected={onContactOptionSelected}
      showLabel={showLabel}
      isLoading={isLoading}
    />
  ) : (
    <StandaloneDropdownContent
      serviceId={serviceId}
      contactLabel={contactLabel}
      onContactOptionSelected={onContactOptionSelected}
      initialSelectedOptionExternalId={initialSelectedOptionExternalId}
      showLabel={showLabel}
      isLoading={isLoading}
    />
  )
}
